// @services/clients/PageCounterApi.ts
import * as E from "fp-ts/Either";
import { pipe } from "fp-ts/function";
import * as t from "io-ts";
import type { TaskEitherTs } from "@ps-bridge/interop";
import type { OptionTs } from "@ps-bridge/interop";
import type { HttpStatusCode } from "@ps-bridge/models/brands/httpStatusCode";
import type { ErrorMessageText } from "@ps-bridge/models/brands/errorMessageText";
import type { DocumentID } from "@ps-bridge/models/brands/documentID";
import { makeDocumentID } from "@ps-bridge/models/brands/documentID";
import type { ProjectID } from "@ps-bridge/models/brands/projectID";
import { makeProjectID } from "@ps-bridge/models/brands/projectID";
import { makeErrorMessageText } from "@ps-bridge/models/brands/errorMessageText";
import * as Option from "@utils/Option";

export type PageCounterRequest = {
    readonly projectId: ProjectID;
    readonly documentId: DocumentID;
};

export type PageCounterResult = {
    readonly documentId: DocumentID;
    readonly filename: string;
    readonly mimeType: string;
    readonly billablePages: number;
    readonly unitPriceCents: number;
    readonly totalCostCents: number;
    readonly currency: string;
    readonly billingOutcome: "success" | "non_pdf" | "failed";
    readonly message: string;
    readonly traceId: string;
};

export type PageCounterClientError =
    | {
          readonly type: "invalidRequest";
          readonly message: ErrorMessageText;
          readonly statusCode: OptionTs<HttpStatusCode>;
      }
    | {
          readonly type: "networkError";
          readonly message: ErrorMessageText;
          readonly statusCode: OptionTs<HttpStatusCode>;
      }
    | {
          readonly type: "responseDecodeError";
          readonly message: ErrorMessageText;
          readonly statusCode: OptionTs<HttpStatusCode>;
      }
    | {
          readonly type: "serviceUnavailable";
          readonly message: ErrorMessageText;
          readonly statusCode: OptionTs<HttpStatusCode>;
      };

export type PageCounterClientErrorType = "invalidRequest" | "networkError" | "responseDecodeError" | "serviceUnavailable";

type PageCounterResultWire = {
    readonly document_id: string;
    readonly filename: string;
    readonly mime_type: string;
    readonly billable_pages: number;
    readonly unit_price_cents: number;
    readonly total_cost_cents: number;
    readonly currency: string;
    readonly billing_outcome: "success" | "non_pdf" | "failed";
    readonly message: string;
    readonly trace_id: string;
};

type PageCounterRequestWire = {
    readonly project_id: string;
    readonly document_id: string;
};

const pageCounterResultCodec = t.type({
    document_id: t.string,
    filename: t.string,
    mime_type: t.string,
    billable_pages: t.number,
    unit_price_cents: t.number,
    total_cost_cents: t.number,
    currency: t.string,
    billing_outcome: t.union([t.literal("success"), t.literal("non_pdf"), t.literal("failed")]),
    message: t.string,
    trace_id: t.string,
});

const pageCounterRequestCodec = t.type({
    project_id: t.string,
    document_id: t.string,
});

const decodeWith = <A, O, I>(codec: t.Type<A, O, I>, input: I, message: string): E.Either<string, A> =>
    E.mapLeft(() => message)(codec.decode(input));

const decodeProjectId = (value: string): E.Either<string, ProjectID> => E.map(makeProjectID)(decodeWith(t.string, value, "Unable to decode project id."));

const decodeDocumentId = (value: string): E.Either<string, DocumentID> => E.map(makeDocumentID)(decodeWith(t.string, value, "Unable to decode document id."));

export const decodePageCounterRequest = (input: PageCounterRequestWire): E.Either<string, PageCounterRequest> =>
    pipe(
        decodeWith(pageCounterRequestCodec, input, "Unable to decode page counter request."),
        E.chain((decoded: { readonly project_id: string; readonly document_id: string }) =>
            pipe(
                decodeProjectId(decoded.project_id),
                E.chain((projectId) =>
                    pipe(
                        decodeDocumentId(decoded.document_id),
                        E.map((documentId): PageCounterRequest => ({ projectId, documentId })),
                    ),
                ),
            ),
        ),
    );

export const decodePageCounterResult = (input: PageCounterResultWire): E.Either<string, PageCounterResult> =>
    E.map((value: PageCounterResultWire): PageCounterResult => ({
        documentId: makeDocumentID(value.document_id),
        filename: value.filename,
        mimeType: value.mime_type,
        billablePages: value.billable_pages,
        unitPriceCents: value.unit_price_cents,
        totalCostCents: value.total_cost_cents,
        currency: value.currency,
        billingOutcome: value.billing_outcome,
        message: value.message,
        traceId: value.trace_id,
    }))(decodeWith(pageCounterResultCodec, input, "Unable to decode page counter response."));

export interface PageCounterClient {
    readonly countUploadedDocument: (request: PageCounterRequest) => TaskEitherTs<PageCounterClientError, PageCounterResult>;
}

export const toPageCounterError = (
    message: string,
    type: PageCounterClientErrorType,
    statusCode: OptionTs<HttpStatusCode> = Option.none(),
): PageCounterClientError => ({
    type,
    message: makeErrorMessageText(message),
    statusCode,
});

export const fromInteropPageCounterClient = (client: {
    readonly countUploadedDocument: (request: PageCounterRequest) => TaskEitherTs<PageCounterClientError, PageCounterResult>;
}): PageCounterClient => ({
    countUploadedDocument: (request) => client.countUploadedDocument(request),
});
